import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import useRealtimeEvents from '../hooks/useRealtimeEvents';

const AuthContext = createContext(null);

const USER_EVENTS = new Set(['user_updated', 'user_blocked', 'user_unblocked', 'user_deleted']);

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const backendUrl = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${backendUrl}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** True when the API says the profile is complete (phone, skill, DUPR, experience). */
export const isProfileComplete = (user) => {
  if (!user) return false;
  if (typeof user.profile_complete === 'boolean') return user.profile_complete;
  // Fallback for older payloads without the flag
  const phone = String(user.phone || '').replace(/\D/g, '');
  const phoneOk = /^(09\d{9}|639\d{9})$/.test(phone);
  const age = Number(user.age);
  const ageOk = Number.isInteger(age) && age >= 5 && age <= 100;
  const genderOk = !!user.gender;
  const skillOk = !!user.skill_level;
  const experienceOk = !!user.playing_experience;
  const duprOk = !!String(user.dupr_rating || '').trim() && !!String(user.dupr_profile_url || '').trim();
  return phoneOk && ageOk && genderOk && skillOk && experienceOk && duprOk;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const navigate = useNavigate();
  const userIdRef = useRef(null);

  useEffect(() => {
    userIdRef.current = user?.user_id || null;
  }, [user?.user_id]);
  
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  const applySession = (data) => {
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      return applySession(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns a structured detail when the email isn't verified yet.
      if (error.response?.status === 403 && detail?.code === 'email_not_verified') {
        const verifyError = new Error(detail.message || 'Please verify your email.');
        verifyError.code = 'email_not_verified';
        verifyError.email = detail.email || email;
        throw verifyError;
      }
      throw new Error(typeof detail === 'string' ? detail : 'Login failed');
    }
  };
  
  const register = async (name, email, password, phone, age, skillLevel, gender, history = {}) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email: email.trim().toLowerCase(),
        password,
        phone,
        age: Number(age),
        gender,
        skill_level: skillLevel,
        playing_experience: history.playing_experience,
        previous_winner: !!history.previous_winner,
        previous_winner_details: history.previous_winner_details || '',
        previous_winner_proof_url: history.previous_winner_proof_url || '',
        previous_winner_proof_filename: history.previous_winner_proof_filename || '',
        previous_winner_proof_file_id: history.previous_winner_proof_file_id || '',
        dupr_rating: history.dupr_rating,
        dupr_profile_url: history.dupr_profile_url,
      });
      // New accounts are NOT auto-logged in — they must verify their email first.
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Registration failed');
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      return applySession(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Verification failed');
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Could not resend code');
    }
  };
  
  const loginWithGoogle = async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { 
        credential: credentialResponse.credential 
      });
      const data = response.data;
      const userData = applySession(data);
      return {
        ...userData,
        is_new_user: !!data.is_new_user,
        profile_complete: data.profile_complete ?? userData?.profile_complete,
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Google login failed');
    }
  };

  const completeProfile = async (payload) => {
    try {
      const response = await axios.put(
        `${API}/users/complete-profile`,
        {
          phone: payload.phone,
          age: payload.age,
          gender: payload.gender,
          skill_level: payload.skill_level,
          playing_experience: payload.playing_experience,
          previous_winner: !!payload.previous_winner,
          previous_winner_details: payload.previous_winner_details || '',
          previous_winner_proof_url: payload.previous_winner_proof_url || '',
          previous_winner_proof_filename: payload.previous_winner_proof_filename || '',
          previous_winner_proof_file_id: payload.previous_winner_proof_file_id || '',
          dupr_rating: payload.dupr_rating,
          dupr_profile_url: payload.dupr_profile_url,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(response.data);
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Could not complete profile');
    }
  };
  
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/');
  };
  
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  // Admin-side changes (skill level, membership, block) land without a reload
  useRealtimeEvents(
    USER_EVENTS,
    useCallback((data) => {
      const targetId = data.user_id || data.user?.user_id;
      if (!targetId || targetId !== userIdRef.current) return;
      if (data.type === 'user_updated') {
        const patch = data.user || {};
        setUser((prev) => (prev ? { ...prev, ...patch } : prev));
        return;
      }
      // Blocked / deleted: pull the authoritative record (may sign the user out)
      checkAuth();
    }, [checkAuth]),
    { enabled: !!user },
  );

  const value = {
    user,
    loading,
    token,
    login,
    register,
    verifyEmail,
    resendVerification,
    loginWithGoogle,
    completeProfile,
    logout,
    updateUser,
    refreshUser: checkAuth,
    isAuthenticated: !!user,
    isProfileComplete: isProfileComplete(user),
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component — incomplete profiles must finish /complete-profile first
export const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onCompletePage = location.pathname === '/complete-profile';
  const complete = isProfileComplete(user);
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { state: { from: location }, replace: true });
      return;
    }
    if (!loading && isAuthenticated && !complete && !onCompletePage) {
      navigate('/complete-profile', {
        replace: true,
        state: { from: location.state?.from || location.pathname + location.search },
      });
    }
  }, [loading, isAuthenticated, complete, onCompletePage, navigate, location]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  if (!complete && !onCompletePage) {
    return (
      <Navigate
        to="/complete-profile"
        replace
        state={{ from: location.state?.from || location.pathname + location.search }}
      />
    );
  }
  
  return children;
};

export default AuthContext;
